import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * A native <select> quantity control for a cart line item.
 *
 * It runs its own cart mutation (cart/change) and dispatches a CartUpdateEvent so
 * every <cart-items-component> re-renders (morph). This is intentionally decoupled
 * from the theme's stepper flow, which assumes DOM order === cart line order — an
 * assumption the pack grouping breaks.
 *
 * Markup:
 *   <cart-line-select data-line="{{ item.index | plus: 1 }}">
 *     <select> ... </select>
 *   </cart-line-select>
 */
class CartLineSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.select?.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onChange);
  }

  #onChange = () => {
    const quantity = parseInt(this.select.value, 10);
    const line = parseInt(this.dataset.line, 10);
    if (Number.isNaN(quantity) || Number.isNaN(line)) return;

    const owner = this.closest('cart-items-component');
    const sectionId = owner?.dataset.sectionId;
    if (!sectionId) return;

    // Collect every cart section on the page so both the /cart page and the drawer refresh.
    const sections = new Set([sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sections.add(el.dataset.sectionId);
    });

    // Prevent double submits while the request is in flight.
    document.querySelectorAll('cart-items-component').forEach((el) => el.classList.add('cart-items-disabled'));

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sections).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((text) => {
        const parsed = JSON.parse(text);
        if (parsed.errors) return;
        this.dispatchEvent(
          new CartUpdateEvent(parsed, sectionId, {
            source: 'cart-line-select',
            sections: parsed.sections,
          })
        );
      })
      .catch((error) => console.error('[cart-line-select]', error))
      .finally(() => {
        document.querySelectorAll('cart-items-component').forEach((el) => el.classList.remove('cart-items-disabled'));
      });
  };
}

if (!customElements.get('cart-line-select')) {
  customElements.define('cart-line-select', CartLineSelect);
}
